import { useState, useEffect } from 'react';
import { UserCheck, X, Check, Eye, AlertCircle, Clock, Mail, Phone, Calendar, MapPin, Briefcase, GraduationCap, Heart, Loader2, FileText, Camera, IdCard } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';

interface PendingProfile {
  _id: string;
  name: string;
  email: string;
  phone: string;
  submittedAt: string;
  faceVerification: any;
  aadharVerification: any;
  profile: {
    age: number;
    gender: string;
    religion: string;
    location: string;
    education: string;
    occupation: string;
    photos: any[];
    profilePhoto: string;
  } | null;
}

interface ProfileDetails {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    createdAt: string;
    adminVerification: any;
    faceVerification: any;
    aadharVerification: any;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
  };
  profile: {
    gender: string;
    dateOfBirth: string;
    age: number;
    maritalStatus: string;
    height: number;
    weight: number;
    alternatePhone: string;
    whatsappNumber: string;
    country: string;
    state: string;
    city: string;
    education: any;
    occupation: string;
    employmentType: string;
    annualIncome: string;
    workLocation: string;
    family: any;
    religion: string;
    caste: string;
    subCaste: string;
    gothra: string;
    motherTongue: string;
    diet: string;
    smoking: string;
    drinking: string;
    horoscope: any;
    bio: string;
    about: string;
    interests: string[];
    hobbies: string[];
    photos: any[];
    profilePhoto: string;
    partnerPreferences: any;
    verification: any;
    stats: any;
  } | null;
}

export function AdminProfileVerification() {
  const [pendingProfiles, setPendingProfiles] = useState<PendingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<ProfileDetails | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadPendingProfiles();
  }, [page]);

  const loadPendingProfiles = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/pending-profiles?page=${page}&limit=20`);
      if (response.data.success) {
        setPendingProfiles(response.data.data.profiles);
        setTotalPages(response.data.data.pagination.pages);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load pending profiles');
    } finally {
      setLoading(false);
    }
  };

  const loadProfileDetails = async (userId: string) => {
    try {
      setDetailsLoading(true);
      const response = await apiClient.get(`/admin/profiles/${userId}/details`);
      if (response.data.success) {
        setSelectedProfile(response.data.data);
        setIsDetailsOpen(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load profile details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedProfile) return;

    try {
      setActionLoading(true);
      const response = await apiClient.post(`/admin/profiles/${selectedProfile.user._id}/approve`, {
        adminNotes
      });

      if (response.data.success) {
        toast.success('Profile approved successfully');
        setIsDetailsOpen(false);
        setSelectedProfile(null);
        setAdminNotes('');
        loadPendingProfiles();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve profile');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProfile || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiClient.post(`/admin/profiles/${selectedProfile.user._id}/reject`, {
        reason: rejectionReason,
        adminNotes
      });

      if (response.data.success) {
        toast.success('Profile rejected');
        setIsDetailsOpen(false);
        setSelectedProfile(null);
        setAdminNotes('');
        setRejectionReason('');
        loadPendingProfiles();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject profile');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <UserCheck className="size-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl">Profile Verification</h2>
            <p className="text-sm text-gray-600">Review and approve user profiles</p>
          </div>
        </div>
        <Button
          onClick={loadPendingProfiles}
          variant="outline"
          size="sm"
        >
          <Loader2 className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 md:p-6 rounded-lg border">
          <div className="flex items-center gap-3">
            <Clock className="size-8 text-amber-600" />
            <div>
              <p className="text-sm text-gray-600">Pending Verification</p>
              <p className="text-2xl">{pendingProfiles.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Profiles List */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="text-lg">Pending Profiles</h3>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="size-8 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Loading profiles...</p>
          </div>
        ) : pendingProfiles.length === 0 ? (
          <div className="p-12 text-center">
            <UserCheck className="size-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No pending profiles</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pendingProfiles.map((profile) => (
                  <tr key={profile._id} className="hover:bg-gray-50">
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      <div>
                        <p className="font-medium">{profile.name}</p>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-1">
                            <Mail className="size-3" />
                            <span>{profile.email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="size-3" />
                            <span>{profile.phone}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      {profile.profile ? (
                        <div className="text-sm space-y-1">
                          <div>{profile.profile.age} yrs, {profile.profile.gender}</div>
                          <div>{profile.profile.religion}</div>
                          <div className="text-gray-600">{profile.profile.location}</div>
                          <div className="text-gray-600">{profile.profile.occupation}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">No profile data</span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Camera className="size-4 text-gray-400" />
                          <Badge variant={profile.faceVerification?.isVerified ? 'success' : 'secondary'}>
                            {profile.faceVerification?.isVerified ? 'Face Verified' : 'Pending'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <IdCard className="size-4 text-gray-400" />
                          <Badge variant={profile.aadharVerification?.isVerified ? 'success' : 'secondary'}>
                            {profile.aadharVerification?.isVerified ? 'Aadhar Verified' : 'Pending'}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      <div className="text-sm text-gray-600">
                        {formatDate(profile.submittedAt)}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4">
                      <Button
                        onClick={() => loadProfileDetails(profile._id)}
                        size="sm"
                        variant="outline"
                      >
                        <Eye className="size-4 mr-2" />
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t flex items-center justify-between">
            <Button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              variant="outline"
              size="sm"
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              variant="outline"
              size="sm"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Profile Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profile Verification Details</DialogTitle>
            <DialogDescription>
              Review all profile information and documents before approval
            </DialogDescription>
          </DialogHeader>

          {selectedProfile && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-3">User Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Name</p>
                    <p className="font-medium">{selectedProfile.user.firstName} {selectedProfile.user.lastName}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium">{selectedProfile.user.email}</p>
                    {selectedProfile.user.isEmailVerified && <Badge variant="success" className="mt-1">Verified</Badge>}
                  </div>
                  <div>
                    <p className="text-gray-600">Phone</p>
                    <p className="font-medium">{selectedProfile.user.phone}</p>
                    {selectedProfile.user.isPhoneVerified && <Badge variant="success" className="mt-1">Verified</Badge>}
                  </div>
                  <div>
                    <p className="text-gray-600">Registered</p>
                    <p className="font-medium">{formatDate(selectedProfile.user.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Profile Details */}
              {selectedProfile.profile && (
                <>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Age & Gender</p>
                        <p className="font-medium">{selectedProfile.profile.age} years, {selectedProfile.profile.gender}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Date of Birth</p>
                        <p className="font-medium">{selectedProfile.profile.dateOfBirth ? formatDate(selectedProfile.profile.dateOfBirth) : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Marital Status</p>
                        <p className="font-medium">{selectedProfile.profile.maritalStatus}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Height / Weight</p>
                        <p className="font-medium">
                          {selectedProfile.profile.height
                            ? `${Math.floor(selectedProfile.profile.height / 12)}'${selectedProfile.profile.height % 12}" (${selectedProfile.profile.height} inches)`
                            : 'N/A'} / {selectedProfile.profile.weight || 'N/A'} kg
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Location</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Country</p>
                        <p className="font-medium">{selectedProfile.profile.country || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">State</p>
                        <p className="font-medium">{selectedProfile.profile.state || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">City</p>
                        <p className="font-medium">{selectedProfile.profile.city || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Education & Career</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Education Level</p>
                        <p className="font-medium">{selectedProfile.profile.education?.level || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Field of Study</p>
                        <p className="font-medium">{selectedProfile.profile.education?.field || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Occupation</p>
                        <p className="font-medium">{selectedProfile.profile.occupation || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Employment Type</p>
                        <p className="font-medium">{selectedProfile.profile.employmentType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Annual Income</p>
                        <p className="font-medium">{selectedProfile.profile.annualIncome || 'N/A'} LPA</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Work Location</p>
                        <p className="font-medium">{selectedProfile.profile.workLocation || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Religion & Community</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Religion</p>
                        <p className="font-medium">{selectedProfile.profile.religion || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Caste</p>
                        <p className="font-medium">{selectedProfile.profile.caste || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Sub Caste</p>
                        <p className="font-medium">{selectedProfile.profile.subCaste || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Gothra</p>
                        <p className="font-medium">{selectedProfile.profile.gothra || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Mother Tongue</p>
                        <p className="font-medium">{selectedProfile.profile.motherTongue || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {selectedProfile.profile.family && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Family Details</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Father's Name</p>
                          <p className="font-medium">{selectedProfile.profile.family.fatherName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Father's Occupation</p>
                          <p className="font-medium">{selectedProfile.profile.family.fatherOccupation || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Mother's Name</p>
                          <p className="font-medium">{selectedProfile.profile.family.motherName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Mother's Occupation</p>
                          <p className="font-medium">{selectedProfile.profile.family.motherOccupation || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Siblings</p>
                          <p className="font-medium">
                            Brothers: {selectedProfile.profile.family.siblings?.brothers || 0},
                            Sisters: {selectedProfile.profile.family.siblings?.sisters || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Family Type</p>
                          <p className="font-medium">{selectedProfile.profile.family.familyType || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Family Status</p>
                          <p className="font-medium">{selectedProfile.profile.family.familyStatus || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Family Values</p>
                          <p className="font-medium">{selectedProfile.profile.family.familyValues || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Lifestyle</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Diet</p>
                        <p className="font-medium">{selectedProfile.profile.diet || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Smoking</p>
                        <p className="font-medium">{selectedProfile.profile.smoking || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Drinking</p>
                        <p className="font-medium">{selectedProfile.profile.drinking || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {selectedProfile.profile.horoscope && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Horoscope Details</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Birth Time</p>
                          <p className="font-medium">{selectedProfile.profile.horoscope.birthTime || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Birth Place</p>
                          <p className="font-medium">{selectedProfile.profile.horoscope.birthPlace || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Manglik Status</p>
                          <p className="font-medium">{selectedProfile.profile.horoscope.manglik || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Raashi</p>
                          <p className="font-medium">{selectedProfile.profile.horoscope.raashi || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Nakshatra</p>
                          <p className="font-medium">{selectedProfile.profile.horoscope.nakshatra || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedProfile.profile.bio && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Bio / Short Intro</h3>
                      <p className="text-sm">{selectedProfile.profile.bio}</p>
                    </div>
                  )}

                  {selectedProfile.profile.about && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">About Me</h3>
                      <p className="text-sm">{selectedProfile.profile.about}</p>
                    </div>
                  )}

                  {/* Partner Preferences */}
                  {selectedProfile.profile.partnerPreferences && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Partner Preferences</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedProfile.profile.partnerPreferences.ageRange && (
                          <div>
                            <p className="text-gray-600">Age Range</p>
                            <p className="font-medium">
                              {selectedProfile.profile.partnerPreferences.ageRange.min || 'Any'} - {selectedProfile.profile.partnerPreferences.ageRange.max || 'Any'} years
                            </p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.heightRange && (
                          <div>
                            <p className="text-gray-600">Height Range</p>
                            <p className="font-medium">
                              {selectedProfile.profile.partnerPreferences.heightRange.min
                                ? (() => {
                                  const cm = selectedProfile.profile.partnerPreferences.heightRange.min;
                                  const totalInches = Math.round(cm / 2.54);
                                  const feet = Math.floor(totalInches / 12);
                                  const inches = totalInches % 12;
                                  return `${feet}'${inches}"`;
                                })()
                                : 'Any'} - {selectedProfile.profile.partnerPreferences.heightRange.max
                                  ? (() => {
                                    const cm = selectedProfile.profile.partnerPreferences.heightRange.max;
                                    const totalInches = Math.round(cm / 2.54);
                                    const feet = Math.floor(totalInches / 12);
                                    const inches = totalInches % 12;
                                    return `${feet}'${inches}"`;
                                  })()
                                  : 'Any'}
                            </p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.maritalStatus && (
                          <div>
                            <p className="text-gray-600">Marital Status</p>
                            <p className="font-medium">
                              {Array.isArray(selectedProfile.profile.partnerPreferences.maritalStatus)
                                ? selectedProfile.profile.partnerPreferences.maritalStatus.join(', ')
                                : selectedProfile.profile.partnerPreferences.maritalStatus}
                            </p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.religion && (
                          <div>
                            <p className="text-gray-600">Religion</p>
                            <p className="font-medium">{selectedProfile.profile.partnerPreferences.religion}</p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.diet && (
                          <div>
                            <p className="text-gray-600">Food Preference</p>
                            <p className="font-medium">{selectedProfile.profile.partnerPreferences.diet}</p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.smoking && (
                          <div>
                            <p className="text-gray-600">Smoking</p>
                            <p className="font-medium">{selectedProfile.profile.partnerPreferences.smoking}</p>
                          </div>
                        )}
                        {selectedProfile.profile.partnerPreferences.drinking && (
                          <div>
                            <p className="text-gray-600">Drinking</p>
                            <p className="font-medium">{selectedProfile.profile.partnerPreferences.drinking}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {(selectedProfile.profile.interests?.length > 0 || selectedProfile.profile.hobbies?.length > 0) && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Interests & Hobbies</h3>
                      <div className="space-y-2 text-sm">
                        {selectedProfile.profile.interests?.length > 0 && (
                          <div>
                            <p className="text-gray-600">Interests</p>
                            <p className="font-medium">{selectedProfile.profile.interests.join(', ')}</p>
                          </div>
                        )}
                        {selectedProfile.profile.hobbies?.length > 0 && (
                          <div>
                            <p className="text-gray-600">Hobbies</p>
                            <p className="font-medium">{selectedProfile.profile.hobbies.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Profile Photos */}
                  {selectedProfile.profile.photos && selectedProfile.profile.photos.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-3">Profile Photos ({selectedProfile.profile.photos.length})</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {selectedProfile.profile.photos.map((photo: any, index: number) => (
                          <div key={index} className="border rounded-lg overflow-hidden">
                            <img
                              src={photo.url || photo}
                              alt={`Profile ${index + 1}`}
                              className="w-full h-48 object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23f0f0f0"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">No Image</text></svg>';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show message if no photos */}
                  {(!selectedProfile.profile.photos || selectedProfile.profile.photos.length === 0) && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> No profile photos have been uploaded yet.
                      </p>
                    </div>
                  )}

                </>
              )}

              {/* Verification Documents */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-3">Verification Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ID Document from Profile */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      ID Document
                      {selectedProfile.profile?.verification?.idDocument?.type && (
                        <span className="ml-1">({selectedProfile.profile.verification.idDocument.type.replace('_', ' ').toUpperCase()})</span>
                      )}
                    </p>
                    {selectedProfile.profile?.verification?.idDocument?.url ? (
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedProfile.profile.verification.idDocument.url}
                          alt="ID Document"
                          className="w-full h-48 object-contain"  // Changed from object-cover
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23f0f0f0"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999">Failed to load</text></svg>';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="border rounded-lg p-8 text-center text-gray-400">
                        <IdCard className="size-8 mx-auto mb-2" />
                        <p className="text-sm">No ID document uploaded</p>
                      </div>
                    )}
                  </div>

                  {/* Face Verification */}
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Face Verification Photo</p>
                    {selectedProfile.user.faceVerification?.imageUrl ? (
                      <div className="border rounded-lg overflow-hidden">
                        <img
                          src={selectedProfile.user.faceVerification.imageUrl}
                          alt="Face verification"
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    ) : (
                      <div className="border rounded-lg p-8 text-center text-gray-400">
                        <Camera className="size-8 mx-auto mb-2" />
                        <p className="text-sm">No face photo uploaded</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Admin Actions */}
              <div className="space-y-4">
                <div>
                  <Label>Admin Notes (Optional)</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add any notes about this profile..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Rejection Reason (Required for rejection)</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide a detailed reason for rejection..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleApprove}
                    disabled={actionLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {actionLoading ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="size-4 mr-2" />
                    )}
                    Approve Profile
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={actionLoading || !rejectionReason.trim()}
                    variant="destructive"
                    className="flex-1"
                  >
                    {actionLoading ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <X className="size-4 mr-2" />
                    )}
                    Reject Profile
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}